import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { showSpinner, closeSpinner, modal, confirm } from "../../../js/modules/Dialogs";
import { setJob } from "../../../services/adminService";
import { addressAutoComplete, findAddress } from "../../../services/geoService";
import ReactSelect from "react-select";
import DatePicker from "react-datepicker";
import { sessionExpired, adminSaveChangesConfirm, adminJobAlreadyClosed, adminJobAlreadyComplete, adminJobAlreadyPaid } from "../../../config/messages";
import { defaultCommission } from "../../../config/constants";
import { toScreen } from "../../../js/modules/Routing";
import { toast } from "react-toastify";
import ReactSlider from "react-slider";
import { CloseIcon } from "../../commonUI/Icons/SvgIcon";

const JobEdit = ({ view, jobs, onClose, props }) => {
  const isEdit = view === "edit";
  const navigate = useNavigate();
  const defaultCommission = 0.2;

  // Initial form state with default values
  const initialFormState = useMemo(
    () => ({
      status: "",
      jobType: "",
      description: "",
      pickupAddress: "",
      dropOffAddress: "",
      dateType: "specific",
      date: "",
      daysOfWeek: [],
      timeRange: [9, 13],
    }),
    []
  );

  const [formData, setFormData] = useState(initialFormState);

  // Initialize form data when jobs prop changes
  useEffect(() => {
    if (jobs?.job) {
      setFormData({
        status: jobs.job.status || "",
        jobType: jobs.job.jobSpec?.serviceType || "",
        description: jobs.job.description || "",
        pickupAddress: jobs?.job.originAddress?.formattedAddress || "",
        dropOffAddress: jobs.job.destinationAddress?.formattedAddress || "",
        dateType: jobs.job.dateType || "specific",
        date: jobs.job.date || "",
        daysOfWeek: jobs.job?.jobSpec?.dateAndTime?.days?.daysOfWeek || [],
        timeRange: jobs.job.timeRange || [9, 13],
      });
    }
  }, [jobs]);

  // Memoize options to prevent unnecessary re-renders
  const statusOptions = useMemo(
    () => [
      { value: "open", label: "Open" },
      { value: "closed", label: "Closed" },
      { value: "paid", label: "Paid" },
      { value: "complete", label: "Complete" },
    ],
    []
  );

  const jobTypeOptions = useMemo(
    () => [
      { value: "housemove", label: "House move" },
      { value: "senditems", label: "Send items" },
      { value: "dispose", label: "Waste disposal" },
      { value: "getitems", label: "Get items" },
    ],
    []
  );

  const dateTypeOptions = useMemo(
    () => [
      { value: "today", label: "Today" },
      { value: "tomorrow", label: "Tomorrow" },
      { value: "thisWeek", label: "This week" },
      { value: "specificDate", label: "On a specific date" },
      { value: "notSure", label: "Not sure" },
    ],
    []
  );

  // Custom styles for ReactSelect
  const selectStyles = useMemo(
    () => ({
      control: (provided) => ({
        ...provided,
        minHeight: "40px",
        height: "40px",
        minWidth: "150px",
        width: "150px",
        padding: "0",
        boxShadow: "none",
        "&:focus-visible": {
          borderColor: "#000000",
          boxShadow: "0 0 0 2px #FFFFFF, 0 0 0 4px #000000",
          outline: "none",
        },
      }),
      valueContainer: (provided) => ({
        ...provided,
        height: "40px",
        padding: "0 8px",
        display: "flex",
        alignItems: "center",
      }),
      input: (provided) => ({
        ...provided,
        margin: "0",
        padding: "0",
      }),
      indicatorsContainer: (provided) => ({
        ...provided,
        height: "40px",
      }),
      option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected
          ? "#f1f5f9"
          : state.isFocused
          ? "#f1f5f9"
          : null,
        color: "#333",
        "&:hover": {
          backgroundColor: "#f1f5f9",
        },
      }),
       indicatorSeparator: (provided) => ({
        ...provided,
        display: "none",
      }),
    }),
    []
  );

  // Event handlers
  const handleStatusChange = useCallback((selectedOption) => {
    if (selectedOption) {
      setFormData((prev) => ({
        ...prev,
        status: selectedOption.value,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        status: "",
      }));
    }
  }, []);

  const handleJobTypeChange = useCallback((selectedOption) => {
    if (selectedOption) {
      setFormData((prev) => ({
        ...prev,
        jobType: selectedOption.value,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        jobType: "",
      }));
    }
  }, []);

  const handleDateTypeChange = useCallback((selectedOption) => {
    if (selectedOption) {
      setFormData((prev) => ({
        ...prev,
        dateType: selectedOption.value,
      }));
    }
  }, []);

  const handleInputChange = useCallback((event) => {
    const { name, value, type, checked } = event.target;
    const inputValue = type === "checkbox" ? checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: inputValue,
    }));
  }, []);

  const handleDayToggle = useCallback((day) => {
    setFormData((prev) => {
      const currentDays = [...prev.daysOfWeek];
      if (currentDays.includes(day)) {
        return { ...prev, daysOfWeek: currentDays.filter((d) => d !== day) };
      } else {
        return { ...prev, daysOfWeek: [...currentDays, day] };
      }
    });
  }, []);

  const handleTimeRangeChange = useCallback((newRange) => {
    setFormData((prev) => ({
      ...prev,
      timeRange: newRange,
    }));
  }, []);

  // Form validation
  const validateForm = useCallback(() => {
    const errors = [];

    if (!formData.status) {
      errors.push("Status is required");
    }

    if (!formData.jobType) {
      errors.push("Job type is required");
    }

    if (!formData.pickupAddress) {
      errors.push("Pickup address is required");
    }

    if (!formData.dropOffAddress) {
      errors.push("Drop-off address is required");
    }

    if (formData.dateType === "specific" && !formData.date) {
      errors.push("Date is required");
    }

    return errors;
  }, [formData]);

  // Save job handler
  const saveJob = useCallback(
    async (event) => {
      if (event) {
        event.preventDefault();
      }

      // Validate form
      const errors = validateForm();
      if (errors.length > 0) {
        toast.error(
          `Please fix the following errors:\n• ${errors.join("\n• ")}`
        );
        return;
      }

      // Confirm before saving
      confirm(false, adminSaveChangesConfirm, "Cancel", "Save", async () => {
        showSpinner();

        try {
          // Prepare job data for API
          let theJob = {
            _id: jobs.job._id,
            short_id: jobs.job.short_id,
            notes: jobs.job.notes,
            originAddress: {
              ...jobs.job.originAddress,
              formattedAddress: formData.pickupAddress,
            },
            destinationAddress: {
              ...jobs.job.destinationAddress,
              formattedAddress: formData.dropOffAddress,
            },
            selectedMav: jobs.selectedMav,
            mavPayment: jobs.job.mavPayment,
            marketingDescription: jobs.job.marketingDescription,
            status: formData.status,
            jobType: formData.jobType,
            description: formData.description,
            dateType: formData.dateType,
            date: formData.date,
            daysOfWeek: formData.daysOfWeek,
            timeRange: formData.timeRange,
            refundRequested: jobs.job.refundRequested,
            refundAuthorisation: jobs.job.refundAuthorisation,
            isPaused: jobs.job.isPaused,
          };

          if (
            !jobs.job?.jobSpec?.specVersion ||
            jobs.job?.jobSpec?.specVersion === 1
          ) {
            theJob.jobSpec = {
              ...jobs.job.jobSpec,
              date: formData.date === "" ? "asap" : formData.date,
            };
          } else {
            theJob.jobSpec = {
              ...jobs.job.jobSpec,
            };
          }

          // Call API to save job
          const resJson = await setJob(theJob);
          closeSpinner();
          toast.success(resJson?.message || "Job successfully updated");
          if (onClose) onClose();
        } catch (error) {
          closeSpinner();
          if (error.response?.status === 401) {
            modal(sessionExpired);
            toScreen(false, navigate, "/logout");
          } else if (error.response?.status === 400) {
            toast.error(error.response?.data?.message || "Bad request.");
          } else {
            toast.error("An error occurred. Please try again.");
          }
          console.error("Error saving job:", error);
        }
      });
    },
    [jobs, formData, validateForm]
  );

  // API response handler (no longer used — kept for reference)
  const saveJob_done = useCallback(
    (resStatus, responseString) => {
      closeSpinner();

      if (resStatus === 401) {
        modal(sessionExpired);
        toScreen(false, navigate, "/logout");
      } else if (resStatus === "400") {
        toast.error(responseString);
      } else if (resStatus === 200) {
        toast.success(responseString || "Job successfully updated");
        if (onClose) onClose();
      } else {
        toast.error("An error occurred. Please try again.");
      }
    },
    [navigate, onClose]
  );

  // Days of week data
  const daysOfWeek = useMemo(
    () => [
      { letter: "M", fullName: "Monday" },
      { letter: "T", fullName: "Tuesday" },
      { letter: "W", fullName: "Wednesday" },
      { letter: "Th", fullName: "Thursday" },
      { letter: "F", fullName: "Friday" },
      { letter: "Sa", fullName: "Saturday" },
      { letter: "S", fullName: "Sunday" },
    ],
    []
  );

  return (
    <div className="right-modal job-edit-modal">
      {/* Modal Header */}
      <div className="modal-header">
        <div className="modal-title">
          <h2>Edit job details</h2>
          <button
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <CloseIcon />
          </button>
        </div>
      </div>

      {/* Modal Body */}
      <div className="modal-body">
        <form onSubmit={saveJob}>
          {/* Job Status Section */}
          <div className="form-section">
            <div className="form-group">
              <label htmlFor="status" className="form-label">
                Job status
              </label>
              <ReactSelect
                inputId="status"
                classNamePrefix="custom-select"
                options={statusOptions}
                name="status"
                placeholder="Select Status"
                value={
                  statusOptions.find((opt) => opt.value === formData?.status) ||
                  null
                }
                onChange={handleStatusChange}
                className="react-select-container"
                styles={selectStyles}
                aria-label="Job status"
              />
            </div>
          </div>

          {/* Details Section */}
          <h3 className="section-title">Details</h3>

          <div className="form-group">
            <label htmlFor="jobType" className="form-label">
              Job type
            </label>
            <ReactSelect
              inputId="jobType"
              classNamePrefix="custom-select"
              options={jobTypeOptions}
              name="jobType"
              placeholder="Select Job Type"
              value={
                jobTypeOptions.find((opt) => opt.value === formData?.jobType) ||
                null
              }
              onChange={handleJobTypeChange}
              className="react-select-container"
              styles={selectStyles}
              aria-label="Job type"
            />
          </div>

          <div className="form-group">
            <label htmlFor="description" className="form-label">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              className="form-control"
              placeholder="Enter job description"
              value={formData.description}
              onChange={handleInputChange}
              rows={4}
              aria-label="Job description"
            />
          </div>

          {/* Addresses Section */}
          <h3 className="section-title">Addresses</h3>

          <div className="form-group">
            <label htmlFor="pickupAddress" className="form-label">
              Pickup address
            </label>
            <input
              type="text"
              id="pickupAddress"
              name="pickupAddress"
              className="form-control"
              placeholder="Enter pickup address"
              value={formData.pickupAddress}
              onChange={handleInputChange}
              required
              aria-label="Pickup address"
            />
          </div>

          <div className="form-group">
            <label htmlFor="dropOffAddress" className="form-label">
              Drop-off address
            </label>
            <input
              type="text"
              id="dropOffAddress"
              name="dropOffAddress"
              className="form-control"
              placeholder="Enter drop-off address"
              value={formData.dropOffAddress}
              onChange={handleInputChange}
              required
              aria-label="Drop-off address"
            />
          </div>

          {/* Date Section */}
          <h3 className="section-title">Date</h3>
          <div className="form-group">
            <label htmlFor="dateType" className="form-label">
              Date type
            </label>
            <ReactSelect
              inputId="dateType"
              classNamePrefix="custom-select"
              options={dateTypeOptions}
              name="dateType"
              placeholder="Select Date Type"
              value={
                dateTypeOptions.find(
                  (opt) => opt.value === formData.dateType
                ) || null
              }
              onChange={handleDateTypeChange}
              className="react-select-container"
              styles={selectStyles}
              aria-label="Date type"
            />
          </div>

          {/* Days of the week */}
          <div className="form-group">
            <label className="form-label">Days of the week</label>
            <div className="days-of-week-container">
              {daysOfWeek.map((day, index) => (
                <button
                  key={index}
                  type="button"
                  className={`day-button ${
                    formData.daysOfWeek.includes(day.letter) ? "active" : ""
                  }`}
                  onClick={() => handleDayToggle(day.letter)}
                  aria-label={day.fullName}
                  title={day.fullName}
                >
                  {day.letter}
                </button>
              ))}
            </div>
          </div>

          {/* Time Range Slider */}
          <div className="form-group">
            <label className="form-label">Time range</label>
            <div className="time-range-container">
              <ReactSlider
                className="time-slider"
                thumbClassName="time-slider-thumb"
                trackClassName="time-slider-track"
                value={formData.timeRange}
                min={7}
                max={23}
                minDistance={1}
                pearling
                withTracks
                onChange={handleTimeRangeChange}
                ariaLabel={["Minimum time", "Maximum time"]}
                ariaValuetext={(state) => `Time: ${state.valueNow}:00`}
              />
              <div className="time-range-labels">
                <span className="time-label">7:00</span>
                <span className="time-window">
                  Time window: {formData.timeRange[0]}:00 -{" "}
                  {formData.timeRange[1]}:00
                </span>
                <span className="time-label">23:00</span>
              </div>
            </div>
          </div>
        </form>
      </div>
      <div className="modal-footer">
        <div className="d-flex align-items-center g-12">
          <button className="btn btn-success" type="button" onClick={saveJob}>
            Save Changes
          </button>
          <button className="btn-outline-light" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default JobEdit;
